import { formatMoney } from '@theme/money-formatting';

/**
 * Тест «Створи свій набір» (sections/set-builder.liquid).
 * Рахує суму обраних товарів, пам'ятає вибір у браузері й додає весь набір у кошик одним запитом.
 * Після вибору товару під ним з'являються кількість і кнопка «Далі»: Enter або клік переносить
 * до наступного питання.
 */
class SetBuilder extends HTMLElement {
  connectedCallback() {
    this.addButton = this.querySelector('[data-add]');
    this.totalElement = this.querySelector('[data-total]');
    this.countElement = this.querySelector('[data-count]');
    this.errorElement = this.querySelector('[data-error]');

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addButton?.addEventListener('click', this.#addToCart);
    window.addEventListener('pageshow', this.#onPageShow);

    this.#restore();
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.addButton?.removeEventListener('click', this.#addToCart);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  get #productInputs() {
    return /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[data-variant-id]'));
  }

  get #boxInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-box]'));
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const step = input.closest('[data-step]');

    if (input.dataset.variantId) {
      this.#setQuantity(input, input.checked ? Number(input.dataset.startQuantity) || 1 : 0);
      if (input.checked) {
        this.#showNext(input);
      } else {
        this.#moveNextToLastSelected(step);
      }
    }
    if (input.checked) this.#setSkipped(step, false);

    this.#update();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;

    const quantityButton = target?.closest('[data-step-quantity]');
    if (quantityButton instanceof HTMLElement) {
      const input = this.#inputOf(quantityButton);
      if (!input) return;

      const quantity = Number(input.dataset.quantity || 0) + Number(quantityButton.dataset.stepQuantity);
      this.#setQuantity(input, quantity);
      if (!input.checked) {
        this.#moveNextToLastSelected(input.closest('[data-step]'));
        input.focus({ preventScroll: true });
      }
      this.#update();
      return;
    }

    const nextButton = target?.closest('[data-next]');
    if (nextButton) {
      this.#goToNextStep(nextButton.closest('[data-step]'));
      return;
    }

    const skipButton = target?.closest('[data-skip]');
    if (skipButton) {
      const step = skipButton.closest('[data-step]');
      const skipped = skipButton.getAttribute('aria-pressed') !== 'true';

      if (skipped && step) {
        for (const input of step.querySelectorAll('input:checked')) {
          if (!(input instanceof HTMLInputElement)) continue;
          if (input.dataset.variantId) this.#setQuantity(input, 0);
          input.checked = false;
        }
        this.#scrollToStep(step.nextElementSibling);
      }

      this.#setSkipped(step, skipped);
      this.#update();
    }
  };

  /**
   * Enter на «−» чи «+» теж означає «Далі»: людина вже обрала кількість і хоче йти далі.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof Element)) return;
    if (!event.target.closest('[data-step-quantity]')) return;

    event.preventDefault();
    this.#goToNextStep(event.target.closest('[data-step]'));
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    this.addButton?.removeAttribute('aria-busy');
    this.#update();
  };

  /** @param {Element} element */
  #inputOf(element) {
    const input = element.closest('.set-builder__item')?.querySelector('input[data-variant-id]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} quantity
   */
  #setQuantity(input, quantity) {
    const max = Number(input.dataset.max) || 99;
    const value = Math.min(Math.max(quantity, 0), max);
    const controls = input.closest('.set-builder__item')?.querySelector('.set-builder__controls');

    input.checked = value > 0;
    input.dataset.quantity = String(value);

    if (!(controls instanceof HTMLElement)) return;
    controls.hidden = value === 0;
    if (value === 0) controls.querySelector('[data-next]')?.setAttribute('hidden', '');

    const output = controls.querySelector('output');
    if (output) output.textContent = String(value);

    const increase = controls.querySelector('[data-step-quantity="1"]');
    if (increase instanceof HTMLButtonElement) increase.disabled = value >= max;
  }

  /**
   * Показує «Далі» лише під щойно обраним товаром кроку й ставить на неї фокус.
   * @param {HTMLInputElement} input
   */
  #showNext(input) {
    const step = input.closest('[data-step]');
    step?.querySelectorAll('[data-next]').forEach((button) => button.setAttribute('hidden', ''));

    const nextButton = input.closest('.set-builder__item')?.querySelector('[data-next]');
    if (!(nextButton instanceof HTMLElement)) return;

    nextButton.removeAttribute('hidden');
    nextButton.focus({ preventScroll: true });
    nextButton.scrollIntoView({ block: 'nearest', behavior: this.#scrollBehavior });
  }

  /**
   * Якщо зняли вибір з товару з кнопкою «Далі», переносить її на останній обраний товар кроку.
   * @param {Element | null} step
   */
  #moveNextToLastSelected(step) {
    if (!step || step.querySelector('[data-next]:not([hidden])')) return;

    const selected = step.querySelectorAll('input[data-variant-id]:checked');
    const last = selected[selected.length - 1];
    last?.closest('.set-builder__item')?.querySelector('[data-next]')?.removeAttribute('hidden');
  }

  /** @param {Element | null} step */
  #goToNextStep(step) {
    step?.querySelectorAll('[data-next]').forEach((button) => button.setAttribute('hidden', ''));

    const next = step?.nextElementSibling;
    if (next) {
      this.#scrollToStep(next);
      return;
    }

    // Після останнього кроку — до кнопки «Додати в кошик»
    if (this.addButton instanceof HTMLElement) {
      this.addButton.focus({ preventScroll: true });
      this.addButton.scrollIntoView({ block: 'nearest', behavior: this.#scrollBehavior });
    }
  }

  /** @param {Element | null} step */
  #scrollToStep(step) {
    if (!step) return;

    step.scrollIntoView({ behavior: this.#scrollBehavior, block: 'start' });
    const heading = step.querySelector('.set-builder__question');
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
  }

  get #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * @param {Element | null} step
   * @param {boolean} skipped
   */
  #setSkipped(step, skipped) {
    step?.querySelector('[data-skip]')?.setAttribute('aria-pressed', String(skipped));
  }

  #selectedItems() {
    return Array.from(this.#productInputs)
      .filter((input) => input.checked)
      .map((input) => ({
        id: Number(input.dataset.variantId),
        quantity: Number(input.dataset.quantity) || 1,
        price: Number(input.dataset.price) || 0,
      }));
  }

  #update() {
    const items = this.#selectedItems();
    const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const count = items.reduce((sum, item) => sum + item.quantity, 0);

    if (this.totalElement) {
      this.totalElement.textContent = formatMoney(total, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'UAH');
    }
    if (this.countElement) this.countElement.textContent = count > 0 ? `(${count})` : '';
    if (this.addButton instanceof HTMLButtonElement && !this.addButton.hasAttribute('aria-busy')) {
      this.addButton.disabled = count === 0;
    }
    if (this.errorElement) this.errorElement.hidden = true;

    this.#save(items);
  }

  /** @param {{ id: number, quantity: number }[]} items */
  #save(items) {
    const state = {
      items: Object.fromEntries(items.map((item) => [item.id, item.quantity])),
      box: this.#boxInput?.checked ?? false,
    };

    try {
      localStorage.setItem(this.#storageKey, JSON.stringify(state));
    } catch {
      // Без сховища тест працює, просто не пам'ятає вибір.
    }
  }

  #restore() {
    let state = null;
    try {
      state = JSON.parse(localStorage.getItem(this.#storageKey) ?? 'null');
    } catch {
      state = null;
    }
    if (!state) return;

    for (const input of this.#productInputs) {
      const quantity = Number(state.items?.[input.dataset.variantId ?? '']);
      if (quantity > 0) this.#setQuantity(input, quantity);
    }

    const boxInput = this.#boxInput;
    if (boxInput && state.box) boxInput.checked = true;
  }

  #clearSaved() {
    try {
      localStorage.removeItem(this.#storageKey);
    } catch {
      // Нічого страшного: збережений вибір просто залишиться.
    }
  }

  get #storageKey() {
    return this.dataset.storageKey ?? 'set-builder';
  }

  #addToCart = async () => {
    const items = this.#selectedItems().map(({ id, quantity }) => ({ id, quantity }));
    if (items.length === 0 || !(this.addButton instanceof HTMLButtonElement)) return;

    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');
    if (this.errorElement) this.errorElement.hidden = true;

    try {
      const response = await fetch(this.dataset.cartAddUrl ?? '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      if (this.#boxInput?.checked && this.dataset.boxAttribute) {
        await fetch(this.dataset.cartUpdateUrl ?? '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [this.dataset.boxAttribute]: this.dataset.boxValue ?? '' } }),
        });
      }

      this.#clearSaved();
      window.location.assign(this.dataset.cartUrl ?? '/cart');
    } catch (error) {
      console.error(error);
      this.addButton.removeAttribute('aria-busy');
      this.addButton.disabled = false;
      if (this.errorElement) this.errorElement.hidden = false;
    }
  };
}

if (!customElements.get('set-builder')) {
  customElements.define('set-builder', SetBuilder);
}
